import classNames from 'classnames';
import { KeyboardEvent, useId, useRef } from 'react';
import { MdAttachFile, MdClose, MdOpenInNew } from 'react-icons/md';
import styles from './FileUpload.module.scss';
import { FileUploadProps } from './types';
import { useFileUpload } from './useFileUpload';

const DEFAULT_ACCEPT = '.csv,.xls,.xlsx';
const DEFAULT_TITLE = 'Novo envio de arquivo';
const DEFAULT_DESCRIPTION = 'Envie seu arquivo em formato .csv, .xls ou .xlsx.\nArraste o arquivo aqui ou clique para selecionar.';
const DEFAULT_SELECT_LABEL = 'Adicionar arquivo';
const DEFAULT_TEMPLATE_LABEL = 'Baixe o modelo de arquivo';

export const FileUpload = ({
  value = null,
  onChange,
  onInvalidFile,
  acceptedFormats,
  accept = DEFAULT_ACCEPT,
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  selectLabel = DEFAULT_SELECT_LABEL,
  templateUrl,
  templateLabel = DEFAULT_TEMPLATE_LABEL,
  onTemplateClick,
  errorMessage,
  disabled = false,
  className,
  inputProps,
}: FileUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const generatedId = useId();
  const inputId = inputProps?.id ?? generatedId;

  const { file, formattedFileSize, handleDragOver, handleDrop, handleInputChange, clearFile } = useFileUpload({
    value,
    onChange,
    onInvalidFile,
    acceptedFormats,
  });

  const openFilePicker = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openFilePicker();
    }
  };

  const showTemplateAction = Boolean(templateUrl || onTemplateClick);

  return (
    <div className={classNames(styles.container, className)}>
      <input
        {...inputProps}
        ref={inputRef}
        id={inputId}
        type="file"
        className={styles.input}
        accept={accept}
        multiple={false}
        disabled={disabled}
        aria-invalid={Boolean(errorMessage)}
        aria-describedby={errorMessage ? `${inputId}-error` : inputProps?.['aria-describedby']}
        onChange={handleInputChange}
      />

      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-label={typeof title === 'string' ? title : 'Selecionar arquivo'}
        aria-describedby={errorMessage ? `${inputId}-error` : undefined}
        className={classNames(styles.dropzone, disabled && styles.disabled, errorMessage && styles.invalid)}
        onClick={openFilePicker}
        onKeyDown={handleKeyDown}
        onDrop={disabled ? undefined : handleDrop}
        onDragOver={disabled ? undefined : handleDragOver}
      >
        <span className={styles.iconCircle} aria-hidden="true">
          <MdAttachFile />
        </span>

        <div className={styles.content}>
          <p className={styles.title}>{title}</p>
          <p className={styles.description}>{description}</p>
        </div>

        {file ? (
          <div className={styles.fileRow}>
            <MdAttachFile className={styles.fileIcon} aria-hidden="true" />
            <span className={styles.fileName} title={file.name}>
              {file.name}
            </span>
            {formattedFileSize ? <span className={styles.fileMeta}>{formattedFileSize}</span> : null}
            <button
              type="button"
              className={styles.removeButton}
              aria-label={`Remover ${file.name}`}
              disabled={disabled}
              onClick={event => {
                event.stopPropagation();
                clearFile();
              }}
            >
              <MdClose />
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={styles.selectButton}
            disabled={disabled}
            onClick={event => {
              event.stopPropagation();
              openFilePicker();
            }}
          >
            <MdAttachFile />
            <span className={styles.selectLabel}>{selectLabel}</span>
          </button>
        )}
      </div>

      {errorMessage ? (
        <p id={`${inputId}-error`} className={styles.errorMessage} role="alert">
          {errorMessage}
        </p>
      ) : null}

      {showTemplateAction &&
        (onTemplateClick ? (
          <button type="button" className={styles.templateAction} onClick={onTemplateClick}>
            <MdOpenInNew />
            {templateLabel}
          </button>
        ) : (
          <a className={styles.templateAction} href={templateUrl} target="_blank" rel="noreferrer">
            <MdOpenInNew />
            {templateLabel}
          </a>
        ))}
    </div>
  );
};
